import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { jobTypes } from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import type { JobTypeItem } from "../../../../services/admin";
import RIcon from "../../../../components/RIcon";

export default function JobsPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [jobs, setJobs] = useState<JobTypeItem[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editJob, setEditJob] = useState<JobTypeItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const loadJobs = useCallback(async () => {
    try {
      setPageLoading(true);
      const data = await jobTypes.list();
      setJobs(data);
    } catch (err) {
      showToast("加载工种列表失败");
      console.error("Failed to load job types:", err);
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "请输入工种名称";
    return errs;
  };

  const handleCreate = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "job_types:write", showToast)) return;
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await jobTypes.create({ name: form.name, description: form.description || undefined });
      setCreateOpen(false);
      setForm({ name: "", description: "" });
      setErrors({});
      showToast("工种创建成功");
      await loadJobs();
    } catch (err) {
      showToast("创建失败，请重试");
      console.error("Failed to create job type:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!ensurePermission(user, "job_types:delete", showToast)) return;
    if (deleteId === null) return;
    try {
      await jobTypes.delete(deleteId);
      setDeleteId(null);
      showToast("工种已删除");
      await loadJobs();
    } catch (err) {
      showToast("删除失败，请重试");
      console.error("Failed to delete job type:", err);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "job_types:write", showToast)) return;
    e.preventDefault();
    if (!editJob) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await jobTypes.update(editJob.id, { name: form.name, description: form.description || undefined });
      setEditJob(null);
      setForm({ name: "", description: "" });
      setErrors({});
      showToast("工种更新成功");
      await loadJobs();
    } catch (err) {
      showToast("更新失败，请重试");
      console.error("Failed to update job type:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">工种管理</h2>
          <p className="text-xs text-[#718096] mt-0.5">管理企业内部各业务工种岗位信息</p>
        </div>
        <button onClick={() => ensurePermission(user, "job_types:write", showToast) && setCreateOpen(true)} className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] text-white rounded-lg text-sm font-medium hover:bg-[#0D9590] cursor-pointer whitespace-nowrap">
          <RIcon name="add-line" />新建工种
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                {["工种名称", "描述", "人数", "操作"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageLoading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-[#718096]">
                    <RIcon name="loader-4-line" className="animate-spin text-lg mr-2" />加载中...
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-[#718096]">暂无工种数据</td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id} className="border-b border-gray-50 hover:bg-[#F4F8FB]/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-[#1A2B3C] whitespace-nowrap">{job.name}</td>
                    <td className="px-4 py-3 text-[#4A5568]">{job.description || "-"}</td>
                    <td className="px-4 py-3 text-[#1A2B3C] font-medium">{job.user_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => ensurePermission(user, "job_types:write", showToast) && (() => { setEditJob(job); setForm({ name: job.name, description: job.description || "" }); setErrors({}); })()}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] cursor-pointer"
                        >
                          <RIcon name="edit-line" className="text-sm" />
                        </button>
                        <button onClick={() => ensurePermission(user, "job_types:delete", showToast) && setDeleteId(job.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 cursor-pointer">
                          <RIcon name="delete-bin-line" className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">新建工种</h3>
              <button onClick={() => setCreateOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">工种名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="如：QA工程师"
                  className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${errors.name ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">描述</label>
                <input
                  type="text"
                  placeholder="工种描述信息"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setCreateOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" disabled={loading} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  {loading ? <><RIcon name="loader-4-line" className="animate-spin" />创建中...</> : "创建工种"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editJob && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑工种</h3>
              <button onClick={() => { setEditJob(null); setForm({ name: "", description: "" }); setErrors({}); }} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">工种名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="如：QA工程师"
                  className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${errors.name ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">描述</label>
                <input
                  type="text"
                  placeholder="工种描述信息"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setEditJob(null); setForm({ name: "", description: "" }); setErrors({}); }} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" disabled={loading} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  {loading ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存修改"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><RIcon name="delete-bin-line" className="text-2xl text-red-500" /></div>
            <h3 className="text-base font-semibold text-[#1A2B3C] mb-1">确认删除工种</h3>
            <p className="text-sm text-[#718096] mb-6">删除后相关人员的工种信息需要重新分配</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleDelete} className="flex-1 h-10 bg-red-500 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
